import { useAtomValue, useSetAtom } from "jotai";
import { paymentChannelAtom, userAtom } from "../utils/store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getPaymentChannels,
  processPaymentWithChannel,
} from "../utils/request";
import { useCallback } from "react";
import { INTERSWITCH_PAYMENT_URL } from "../utils/constants";
import { App } from "antd";

export const useInitiatePayment = () => {
  const { notification } = App.useApp();
  const user = useAtomValue(userAtom);
  const setPaymentChannel = useSetAtom(paymentChannelAtom);

  const paymentChannelsQuery = useQuery({
    queryKey: ["paymentChannels"],
    queryFn: getPaymentChannels,
  });

  const processPaymentMutation = useMutation({
    mutationFn: (payload: PaymentPayloadWithChannel) =>
      processPaymentWithChannel(payload),
  });

  const makePayment = useCallback(
    async (payload: PaymentPayloadWithChannel) => {
      setPaymentChannel(payload.channel);
      try {
        let payloadToSend: ProcessPaymentPayloadWithChannel = {
          amount: payload.amount,
          returnUrl: payload.returnUrl,
          applicantId: user?.applicantId ?? "",
          paymentType: payload.paymentType,
        };

        const response = await processPaymentMutation.mutateAsync({
          ...payloadToSend,
          channel: payload.channel,
        });
        const paymentResponse = response.data as PaymentResponse;

        const redirectPayload = JSON.parse(
          paymentResponse.responsePayLoad,
        ) as RedirectData;
        if (payload.channel?.toLowerCase() === "interswitch") {
          const interswitchData =
            redirectPayload?.Data as InterswitchRedirectData;
          handleInterswitchRedirect(interswitchData);
          return;
        }

        const redirectUrl = redirectPayload?.RedirectURL;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } catch (error: any) {
        notification.error({
          message: "Payment Error",
          description: error?.response?.data?.message,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processPaymentMutation],
  );

  const handleInterswitchRedirect = (data: InterswitchRedirectData) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = INTERSWITCH_PAYMENT_URL;
    form.style.display = "none";

    Object.entries(data).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  return {
    makePayment,
    isLoading: processPaymentMutation.isPending,
    paymentChannels: paymentChannelsQuery.data?.data as PaymentChannel[],
    isLoadingChannels: paymentChannelsQuery.isLoading,
  };
};
